import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FinancialRefreshProvider } from './contexts/FinancialRefreshContext';
import { AppLayout } from './components/layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleBasedRoute, { AdminRoute } from './components/auth/RoleBasedRoute';
import OnboardingCheck from './components/auth/OnboardingCheck';
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import InviteActivation from './components/auth/InviteActivation';
import CompleteProfile from './pages/CompleteProfile';
import DriverVehiclePage from './pages/DriverVehiclePage';
import DriverPortal from './pages/DriverPortal';
import CompanyRegistration from './components/onboarding/CompanyRegistration';
import { OnboardingWizard } from './components/onboarding';
import LandingPage from './pages/LandingPage';
import ApplyInvitePage from './pages/apply/ApplyInvitePage';
import ApplyWizardPage from './pages/apply/ApplyWizardPage';
import ApplyStatusPage from './pages/apply/ApplyStatusPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import { ToastProvider } from './components/ui/ToastProvider';
import {
  Dashboard,
  Vehicles,
  VehicleInspection,
  Drivers,
  Settings,
  Compliance,
  Documents,
  FinancialDashboard,
  LoadsPage
} from './pages';
import './App.css';

// Component to handle root route based on auth status
const RootRoute = () => {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
    </div>;
  }
  
  return currentUser ? <Navigate to="/dashboard" replace /> : <LandingPage />;
};

function App() {
  return (
    <AuthProvider>
      <FinancialRefreshProvider>
        <ToastProvider>
        <Router>
          <OnboardingCheck>
            <Routes>
          {/* Root route - Landing page or Dashboard */}
          <Route path="/" element={<RootRoute />} />

          {/* Public routes */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/activate-invite" element={<InviteActivation />} />

          {/* Driver application (applicant side) */}
          <Route path="/apply/invite/:token" element={<ApplyInvitePage />} />
          <Route path="/apply" element={
            <ProtectedRoute>
              <ApplyWizardPage />
            </ProtectedRoute>
          } />
          <Route path="/apply/status" element={
            <ProtectedRoute>
              <ApplyStatusPage />
            </ProtectedRoute>
          } />
          <Route path="/complete-profile" element={
            <ProtectedRoute>
              <CompleteProfile />
            </ProtectedRoute>
          } />
          <Route path="/driver-vehicle" element={
            <ProtectedRoute>
              <DriverVehiclePage />
            </ProtectedRoute>
          } />
          <Route path="/driver-portal" element={
            <ProtectedRoute>
              <DriverPortal />
            </ProtectedRoute>
          } />
          <Route path="/register" element={<CompanyRegistration />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="/onboarding/welcome" element={<OnboardingWizard startStep={0} />} />
          <Route path="/onboarding/company" element={<OnboardingWizard startStep={1} />} />
          <Route path="/onboarding/drivers" element={<OnboardingWizard startStep={2} />} />
          <Route path="/onboarding/vehicles" element={<OnboardingWizard startStep={3} />} />
          <Route path="/onboarding/documents" element={<OnboardingWizard startStep={4} />} />
          <Route path="/onboarding/dashboard" element={<OnboardingWizard startStep={5} />} />
          
          {/* Protected routes with shared layout */}
          <Route element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/vehicles" element={<Vehicles />} />
            <Route path="/compliance" element={<Compliance />} />
          </Route>

          <Route element={
            <RoleBasedRoute requiredPermissions={['loads']}>
              <AppLayout />
            </RoleBasedRoute>
          }>
            <Route path="/loads" element={<LoadsPage />} />
          </Route>

          <Route element={
            <RoleBasedRoute requiredPermissions={['financial']}>
              <AppLayout />
            </RoleBasedRoute>
          }>
            <Route path="/financial" element={<FinancialDashboard />} />
          </Route>

          <Route element={
            <RoleBasedRoute requiredPermissions={['canViewAllDrivers']}>
              <AppLayout />
            </RoleBasedRoute>
          }>
            <Route path="/drivers" element={<Drivers />} />
          </Route>

          <Route element={
            <RoleBasedRoute requiredRoles={['admin', 'manager', 'super_admin']}>
              <AppLayout />
            </RoleBasedRoute>
          }>
            <Route path="/drivers/applications/:id" element={<ApplicationDetailPage />} />
          </Route>

          <Route element={
            <RoleBasedRoute requiredPermissions={['canEditDocuments']}>
              <AppLayout />
            </RoleBasedRoute>
          }>
            <Route path="/documents" element={<Documents />} />
          </Route>

          <Route element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route path="/vehicles/:vehicleId/inspection/:inspectionType?" element={<VehicleInspection />} />
          </Route>

          <Route element={
            <AdminRoute>
              <AppLayout />
            </AdminRoute>
          }>
            <Route path="/settings" element={<Settings />} />
          </Route>
          
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </OnboardingCheck>
      </Router>
        </ToastProvider>
      </FinancialRefreshProvider>
    </AuthProvider>
  );
}

export default App;
